import { Alert as AlertAD, type AlertProps } from 'antd';
import cs from 'classnames';
import type { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveClassNames } from '../utils';
import styles from './alert.module.css';
import { useAlertIcon } from './hooks';

export type AlertProperties = {
  type?: AlertType;
} & Omit<ComponentProps<typeof AlertAD>, 'type'>;

export type AlertType = 'neutral' | AlertProps['type'];

const Alert = ({
  banner,
  className,
  classNames,
  description,
  showIcon = true,
  title,
  type,
  ...rest
}: AlertProperties) => {
  const { t } = useTranslation();
  const isNeutral = type === 'neutral';
  const resolvedIcon = useAlertIcon({ type });

  const alertClassName = cs(styles.alert, className, {
    [styles['alert--banner'] ?? '']: banner,
    [styles['alert--neutral'] ?? '']: isNeutral,
  });

  const sharedAlertProperties = {
    className: alertClassName,
    classNames: resolveClassNames(classNames, (resolvedClassNames) => ({
      ...resolvedClassNames,
      section: cs(styles['alert--section'], resolvedClassNames?.section),
    })),
    description: typeof description == 'string' ? t(description) : description,
    icon: resolvedIcon,
    showIcon,
    title: typeof title == 'string' ? t(title) : title,
    type: isNeutral ? undefined : type,
    ...rest,
  };

  if (!banner) {
    return <AlertAD {...sharedAlertProperties} />;
  }

  return (
    <div
      className={cs(styles['banner-wrapper'], {
        [styles['banner-border-error'] ?? '']: type === 'error',
        [styles['banner-border-info'] ?? '']: type === 'info',
        [styles['banner-border-neutral'] ?? '']: isNeutral,
        [styles['banner-border-success'] ?? '']: type === 'success',
        [styles['banner-border-warning'] ?? '']: type === 'warning',
      })}
    >
      <AlertAD banner={banner} {...sharedAlertProperties} />
    </div>
  );
};

export default Alert;
